/**
 * Custom Hook - useModels
 * Manages model-related state and operations
 */

import { useState, useEffect, useCallback } from 'react';
import { Model } from '../models/Model';
import { modelService } from '../services/ModelService';

export function useModels() {
  // const [models, setModels] = useState<Model[]>([]);
  const [models, setModels] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Load all models
   */
  const loadModels = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await modelService.getAllModels();
      setModels(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load models');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Deploy a model
   */
  const deployModel = useCallback(async (id: string) => {
    try {
      const updatedModel = await modelService.deployModel(id);
      if (updatedModel) {
        setModels(prev => prev.map(m => m.id === id ? updatedModel : m));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deploy model');
      throw err;
    }
  }, []);

  /**
   * Create a model
   */
  const createModel = useCallback(async (formData: any) => {
    try {
      const newModel = await modelService.createModel(formData);
      setModels(prev => [...prev, newModel]);
      return newModel;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create model');
      throw err;
    }
  }, []);

  /**
   * Update a model
   */
  const updateModel = useCallback(async (id: string, formData: any) => {
    try {
      const updatedModel = await modelService.updateModel(id, formData);
      if (updatedModel) {
        setModels(prev => prev.map(m => m.id === id ? updatedModel : m));
      }
      return updatedModel;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update model');
      throw err;
    }
  }, []);

  /**
   * Delete a model
   */
  const deleteModel = useCallback(async (id: string) => {
    try {
      const success = await modelService.deleteModel(id);
      if (success) {
        setModels(prev => prev.filter(m => m.id !== id));
      }
      return success;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete model');
      throw err;
    }
  }, []);

  // Load models on mount
  useEffect(() => {
    loadModels();
  }, [loadModels]);

  return {
    models,
    loading,
    error,
    deployModel,
    createModel,
    updateModel,
    deleteModel,
    refreshModels: loadModels,
  };
}
